import { Dispatch, SetStateAction } from "react";
import axios from "axios";
import { useAreYouSure } from "components/areYouSure";
import { Avatar } from "components/avatar";
import { IconButton, NoStyleA } from "components/button";
import DataLabel from "components/dataLabel";
import Dropdown from "components/dropdown";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import { formatMmDdYyyy } from "../../utils/formatDate";
import { RiMore2Line } from "react-icons/ri";
import { useMutation, useQueryClient } from "react-query";
import { TProperty } from "types/property";
import NewPropertyModal from "./newPropertyModal";
import PropertyTagList from "views/property/property-tag-list";
import { useTranslation } from 'react-i18next';
import { IPropertySelected } from "./";
import Spinner from "components/spinner";

type Props = {
  property: TProperty;
  customerId?: string;
  stripped?: boolean;
  selectedProperties?: IPropertySelected[];
  setSelectedProperties?: Dispatch<SetStateAction<IPropertySelected[]>>;
  isDefaultBilling?: boolean;
  defaultBillingLabel?: string;
};

export default function PropertyCon({ property, customerId, stripped, selectedProperties, setSelectedProperties, isDefaultBilling, defaultBillingLabel }: Props) {
  const { t } = useTranslation();
  const tBase = "views.properties.propertyCon";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const modal = useModalContext(),
    areYouSure = useAreYouSure(),
    queryClient = useQueryClient(),
    snackbar = useSnackbarContext();

  const deleteProp = useMutation(
    async () =>
      await axios
        .delete(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/property/${property.id}
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        snackbar.showSnackbar(tr("Property Deleted!"));
      },
      onError: () => {
        modal.setModal({
          component: (
            <div>
              {tr("The property has information attached that can't be removed")}
            </div>
          ),
          label: tr("Error"),
        });
      },
    }
  );
  const setAsBillingAddress = useMutation(
    async () =>
      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${customerId}/billing_address`,
          { propertyId: property.id }
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["customer", customerId]);
        queryClient.invalidateQueries(["customer", "editing", customerId]);
        snackbar.showSnackbar(tr("Default billing address updated"));
      },
      onError: () => {
        snackbar.showSnackbar(tr("Failed to update billing address"));
      },
    }
  );

  const propertyMenuButtons = [
    ...(customerId && !isDefaultBilling
      ? [
          {
            label: tr("Set as default billing address"),
            onClick: () => setAsBillingAddress.mutate(),
          },
        ]
      : []),
    {
      label: tr("Edit"),
      onClick: () =>
        modal.setModal({
          component: (
            <NewPropertyModal customerId={customerId} property={property} />
          ),
          label: `${tr("Property")} #${property.id}`,
        }),
    },
    {
      label: tr("Delete"),
      onClick: () => areYouSure.activate(() => deleteProp.mutate()),
    },
  ];

  return (
    <div
      key={property.id}
      className="flex flex-col py-4 !border-solid !border-slate-200"
    >
      <div className="flex justify-between">
        <div className="flex flex-col">
          <div className="space-x-2 text-sm text-slate-500">
            <span>
              {tr("Property")} #{property.id}
            </span>
            {isDefaultBilling && defaultBillingLabel ? (
              <span className="ml-2 text-xs font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded">
                {defaultBillingLabel}
              </span>
            ) : null}
            <span>|</span>
            <span>
              {property.date
                ? `${tr("Most recent appointment")}: ${formatMmDdYyyy(
                    property.date
                  )}`
                : tr("No appointment")}
            </span>
          </div>
          <div className="flex flex-row">
            {!stripped ? (
              <NoStyleA
                className="text-lg !text-blue-700 hover:!underline !self-start"
                to={`/property/${property.id}`}
              >
                {property.full_address}
              </NoStyleA>
            ) : (
              <span className="text-lg !text-blue-700  !self-start">
                {property.full_address}
              </span>
            )}

            <div className="text-sm">
              <PropertyTagList tags={property.tags} />
            </div>
          </div>
          <span className="italic">{property.name}</span>
        </div>
        {!stripped ? (
          <div className="flex items-center space-x-3">
            {setAsBillingAddress.isLoading ? <Spinner size={16} /> : null}
            <Dropdown
              reference={
                <IconButton square>
                  <RiMore2Line />
                </IconButton>
              }
              data={[
                {
                  button: propertyMenuButtons,
                },
              ]}
            />
          </div>
        ) : (
          <div>
            <input
              type="checkbox"
              className="w-5 h-5"
              checked={!!selectedProperties?.some(p => p.id === property.id.toString())}
              onChange={(e) => {
                const isChecked = e.currentTarget.checked;
                if (!setSelectedProperties) return;
                const billedToEmail = property.billed_to?.email;
                const emails = [...new Set([billedToEmail])];
                setSelectedProperties((prev) =>
                  isChecked
                    ? [
                        ...prev,
                        {
                          id: property.id.toString(),
                          customer_id: property.fk_bcustomer_id?.toString() || "",
                          address: property.address,
                          city: property.city,
                          state: property.state,
                          zipcode: property.zipcode,
                          zip_ext: property.zip_ext,
                          emails: emails.filter((email): email is string => !!email),
                        },
                      ]
                    : prev.filter((p) => p.id !== property.id.toString())
                );
              }}
            />
          </div>
        )}
      </div>

      <div className="flex mt-2 space-x-8">
        {property.owner && (
          <DataLabel title={tr("Owner")}>
            {!stripped ? (
              <NoStyleA to={`/customers/${property.owner?.id}`}>
                <Avatar showLabel size="xsmall">
                  {property.owner?.name}
                </Avatar>
              </NoStyleA>
            ) : (
              <Avatar showLabel size="xsmall">
                {property.owner?.name}
              </Avatar>
            )}

          </DataLabel>
        )}
        {property.billed_to && (
          <DataLabel title={tr("Billed To")}>

            {!stripped ? (
              <NoStyleA to={`/customers/${property.billed_to?.id}`}>
                <Avatar showLabel size="xsmall">
                  {property.billed_to?.name}
                </Avatar>
              </NoStyleA>
            ) : (
              <Avatar showLabel size="xsmall">
                {property.billed_to?.name}
              </Avatar>
            )}
          </DataLabel>
        )}
      </div>
    </div>
  );
}
